export function observeRenderReadiness(root: HTMLElement, onError: (message: string) => void): () => void {
  let revision = 0;
  let disposed = false;
  let resources = new AbortController();
  const check = async () => {
    const current = ++revision;
    resources.abort();
    resources = new AbortController();
    const signal = resources.signal;
    root.dataset.ready = "false";
    delete root.dataset.renderError;
    const failed = root.querySelector<HTMLElement>("[data-grid-resource-error]");
    if (failed) {root.dataset.renderError = failed.dataset.gridResourceError; onError(root.dataset.renderError); return;}
    if (root.querySelector('[data-banner-loading="true"]')) return;
    const images = Array.from(root.querySelectorAll<HTMLImageElement>(".template img[src]"))
      .filter((img) => !img.closest(".tools"));
    const videos = Array.from(root.querySelectorAll<HTMLVideoElement>(".template video[src]"));
    const failure = (element: Element, kind: string, source: string) => new Error(`${element.closest<HTMLElement>("[data-grid-card]").dataset.gridCard}: ${kind} failed: ${source}`);
    const videoReady = async (video: HTMLVideoElement) => {
      video.autoplay = false;
      video.pause();
      if (video.currentTime !== 0) video.currentTime = 0;
      if (video.error) throw failure(video, "Video", video.src);
      if (video.readyState >= 2 && !video.seeking) return;
      await new Promise<void>((resolve, reject) => {
        const cleanup = () => {clearTimeout(timer); video.removeEventListener("loadeddata", ready); video.removeEventListener("seeked", ready); video.removeEventListener("error", failed); signal.removeEventListener("abort", aborted);};
        const ready = () => {if (video.readyState >= 2 && !video.seeking) {cleanup(); resolve();}};
        const failed = () => {cleanup(); reject(failure(video, "Video", video.src));};
        const aborted = () => {cleanup(); reject(new Error("Readiness superseded"));};
        const timer = setTimeout(failed, 20000);
        video.addEventListener("loadeddata", ready); video.addEventListener("seeked", ready); video.addEventListener("error", failed); signal.addEventListener("abort", aborted, {once: true});
        ready();
      });
    };
    const cssImages: Promise<void>[] = [];
    const seen = new Set<string>();
    for (const element of Array.from(root.querySelectorAll<HTMLElement>(".template, .template *"))) {
      if (element.closest(".tools")) continue;
      const style = getComputedStyle(element);
      for (const value of [style.backgroundImage, style.maskImage]) {
        for (const match of value.matchAll(/url\(["']?([^"')]+)["']?\)/g)) {
          const url = match[1];
          if (url.startsWith("#") || seen.has(url)) continue;
          seen.add(url);
          const image = new Image(); image.src = url;
          cssImages.push(image.decode().catch(() => {throw failure(element, "CSS image", url);}));
        }
      }
    }
    try {
      await Promise.all([document.fonts.ready, ...videos.map(videoReady), ...cssImages, ...images.map((img) => img.decode().catch(() => {
        throw new Error(`${img.closest<HTMLElement>("[data-grid-card]").dataset.gridCard}: Image failed: ${img.getAttribute("src")}`);
      }))]);
      if (!disposed && current === revision) {root.dataset.ready = "true"; onError("");}
    } catch (error) {
      if (!disposed && current === revision) {
        root.dataset.renderError = error instanceof Error ? error.message : "Resource failed";
        onError(root.dataset.renderError);
      }
    }
  };
  const observer = new MutationObserver(() => {void check();});
  observer.observe(root, {subtree: true, childList: true, attributes: true, attributeFilter: ["src", "data-banner-loading", "data-grid-resource-error"]});
  void check();
  return () => {disposed = true; revision++; resources.abort(); observer.disconnect();};
}
